import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Paper,
  alpha,
  Collapse,
  IconButton,
  Tooltip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import BusinessIcon from '@mui/icons-material/Business';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BarChartIcon from '@mui/icons-material/BarChart';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { VIEW_MODES } from '../constants';
import {
  StocksView,
  SectorsView,
  AIInsightsSection,
  StocksListSkeleton
} from '../components/stocks';
import { useStocks, useSectors, useValuationStats, useAIInsights } from '../hooks/useStocks';

/* ── Stat Tile Component ─────────────────────────────── */
const StatTile = ({ item, isActive, onClick }) => (
  <Paper elevation={0} onClick={onClick}
    sx={{
      position: 'relative', overflow: 'hidden',
      borderRadius: 2.5, border: `1px solid ${alpha(item.color, isActive ? 0.4 : 0.15)}`,
      bgcolor: isActive ? alpha(item.color, 0.04) : '#fff',
      p: '14px 16px', transition: 'all 0.2s', flex: 1, minWidth: 140,
      cursor: onClick ? 'pointer' : 'default',
      '&:hover': onClick ? {
        borderColor: alpha(item.color, 0.35),
        boxShadow: `0 4px 16px ${alpha(item.color, 0.1)}`,
        transform: 'translateY(-1px)',
      } : {},
      '&::before': isActive ? {
        content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: 3,
        background: `linear-gradient(90deg, ${item.color}, ${alpha(item.color, 0.5)})`,
        borderRadius: '10px 10px 0 0',
      } : {},
    }}>
    {/* Decorative corner */}
    <Box sx={{ position: 'absolute', top: 0, right: 0, width: 64, height: 64,
      background: `radial-gradient(circle at top right, ${alpha(item.color, 0.06)} 0%, transparent 70%)`,
      pointerEvents: 'none' }} />
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box sx={{ width: 36, height: 36, borderRadius: 2, flexShrink: 0,
        background: `linear-gradient(135deg, ${item.color} 0%, ${alpha(item.color, 0.7)} 100%)`,
        boxShadow: `0 3px 8px ${alpha(item.color, 0.25)}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
        {item.icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
          <Typography sx={{ fontWeight: 800, fontSize: '1.25rem', color: '#0f172a', lineHeight: 1.1,
            fontVariantNumeric: 'tabular-nums' }}>
            {item.value?.toLocaleString()}
          </Typography>
          {item.sub && (
            <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 500 }}>{item.sub}</Typography>
          )}
        </Box>
        <Typography sx={{ fontSize: '0.68rem', color: '#64748b', fontWeight: 600,
          textTransform: 'uppercase', letterSpacing: '0.5px', lineHeight: 1.3, mt: 0.2 }}>
          {item.label}
        </Typography>
      </Box>
    </Box>
  </Paper>
);

const StocksList = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const [viewMode, setViewMode] = useState(VIEW_MODES.STOCKS);
  const [searchTerm, setSearchTerm] = useState('');
  const [exchangeFilter, setExchangeFilter] = useState('');
  const [valuationFilter, setValuationFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [showAI, setShowAI] = useState(false);

  const { data: stocksData, isLoading: stocksLoading, error: stocksError } = useStocks({
    page,
    rowsPerPage,
    searchTerm,
    exchangeFilter,
    valuationFilter,
    enabled: viewMode === VIEW_MODES.STOCKS
  });
  
  const { data: sectors, isLoading: sectorsLoading, error: sectorsError } = useSectors(
    viewMode === VIEW_MODES.SECTORS
  );
  
  const { data: stats } = useValuationStats();
  const { data: aiInsights, isLoading: aiLoading } = useAIInsights(3);

  const stocks = stocksData?.stocks || [];
  const total = stocksData?.total || 0;
  const sectorsData2 = Array.isArray(sectors) ? sectors : [];
  const loading = stocksLoading || sectorsLoading;

  const error = stocksError || sectorsError;
  const errorMessage = error ? (error.message || error.toString()) : null;

  // Restore scroll position on mount
  useEffect(() => {
    const scrollKey = `scroll_${location.pathname}`;
    const savedScroll = sessionStorage.getItem(scrollKey);
    if (savedScroll) {
      requestAnimationFrame(() => {
        const mainContent = document.querySelector('.main-content');
        if (mainContent) {
          mainContent.scrollTop = parseInt(savedScroll, 10);
        } else {
          window.scrollTo(0, parseInt(savedScroll, 10));
        }
        sessionStorage.removeItem(scrollKey);
      });
    }
  }, [location.pathname]);

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); };
  const handleSearchChange = (e) => { setSearchTerm(e.target.value); setPage(0); };
  const handleExchangeChange = (e) => { setExchangeFilter(e.target.value); setPage(0); };

  const handleStockClick = (ticker) => {
    const scrollKey = `scroll_${location.pathname}`;
    const mainContent = document.querySelector('.main-content');
    const scrollPos = mainContent ? mainContent.scrollTop : window.scrollY;
    sessionStorage.setItem(scrollKey, scrollPos.toString());
    
    navigate(`/stocks/${ticker}`, {
      state: { from: `${location.pathname}${location.search}${location.hash}`, viewMode, searchTerm, exchangeFilter, valuationFilter, page, rowsPerPage }
    });
  };

  const handleValuationCardClick = (type) => {
    if (valuationFilter === type) {
      setValuationFilter('');
    } else {
      setValuationFilter(type);
      setViewMode(VIEW_MODES.STOCKS);
    }
    setPage(0);
  };

  if (loading && stocks.length === 0 && sectorsData2.length === 0) {
    return <StocksListSkeleton />;
  }

  const statItems = stats ? [
    { key: 'total', label: t('vnstock.stocks.valuedStocks'), value: stats.with_valuation,
      sub: `/ ${stats.total_stocks.toLocaleString()}`, color: '#3b82f6',
      icon: <BarChartIcon sx={{ fontSize: 18 }} />, clickable: false },
    { key: 'undervalued', label: t('vnstock.stocks.undervalued'), value: stats.undervalued,
      color: '#22c55e', icon: <TrendingUpIcon sx={{ fontSize: 18 }} />, clickable: true },
    { key: 'neutral', label: t('vnstock.stocks.neutral'), value: stats.neutral,
      color: '#f59e0b', icon: <AccountBalanceIcon sx={{ fontSize: 18 }} />, clickable: true },
    { key: 'overvalued', label: t('vnstock.stocks.overvalued'), value: stats.overvalued,
      color: '#ef4444', icon: <TrendingDownIcon sx={{ fontSize: 18 }} />, clickable: true }
  ] : [];

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      {/* ── HEADER ────────────────────────────────────── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 42, height: 42, borderRadius: 2.5, flexShrink: 0,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: `0 3px 10px ${alpha('#667eea', 0.3)}`,
          }}>
            <ShowChartIcon sx={{ fontSize: 22, color: '#fff' }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: '#1e293b', lineHeight: 1.2, letterSpacing: '-0.02em' }}>
              {t('vnstock.stocks.title')}
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 500, lineHeight: 1.3, mt: 0.3 }}>
              {t('vnstock.stocks.subtitle')}
            </Typography>
          </Box>
        </Box>

        {/* Controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          {viewMode === VIEW_MODES.STOCKS && (
            <TextField
              size="small"
              placeholder={t('vnstock.stocks.searchPlaceholder')}
              value={searchTerm}
              onChange={handleSearchChange}
              sx={{
                width: { xs: '100%', sm: 220 },
                '& .MuiOutlinedInput-root': {
                  bgcolor: '#fff', borderRadius: 2, fontSize: '0.85rem', height: 36,
                  '& fieldset': { borderColor: '#e2e8f0' },
                  '&:hover fieldset': { borderColor: '#667eea' },
                  '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: 2 },
                }
              }}
              slotProps={{ input: { startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#94a3b8', fontSize: 18 }} />
                </InputAdornment>
              )}}}
            />
          )}

          {viewMode === VIEW_MODES.STOCKS && (
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <Select
                value={exchangeFilter} onChange={handleExchangeChange} displayEmpty
                sx={{
                  bgcolor: '#fff', borderRadius: 2, fontSize: '0.85rem', height: 36,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#667eea' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#667eea' },
                }}
                MenuProps={{ PaperProps: { sx: { borderRadius: 2, mt: 0.5 } } }}
              >
                <MenuItem value="">{t('vnstock.stocks.allExchanges')}</MenuItem>
                <MenuItem value="HOSE">HOSE</MenuItem>
                <MenuItem value="HNX">HNX</MenuItem>
                <MenuItem value="UPCOM">UPCOM</MenuItem>
              </Select>
            </FormControl>
          )}

          <ToggleButtonGroup
            value={viewMode} exclusive onChange={(_, v) => v && setViewMode(v)} size="small"
            sx={{ '& .MuiToggleButton-root': {
              borderColor: '#e2e8f0', px: 1.5, py: 0.5, fontSize: '0.8rem', fontWeight: 600, textTransform: 'none',
              '&.Mui-selected': { bgcolor: '#667eea', color: 'white', borderColor: '#667eea',
                '&:hover': { bgcolor: '#5a6fd6' } }
            }}}
          >
            <ToggleButton value={VIEW_MODES.STOCKS}>
              <ShowChartIcon sx={{ mr: 0.5, fontSize: 16 }} />{t('vnstock.stocks.viewStocks')}
            </ToggleButton>
            <ToggleButton value={VIEW_MODES.SECTORS}>
              <BusinessIcon sx={{ mr: 0.5, fontSize: 16 }} />{t('vnstock.stocks.viewSectors')}
            </ToggleButton>
          </ToggleButtonGroup>

          {aiInsights && aiInsights.count > 0 && !valuationFilter && (
            <Tooltip title="AI Insights">
              <IconButton size="small" onClick={() => setShowAI(!showAI)}
                sx={{ color: showAI ? '#667eea' : '#94a3b8',
                  bgcolor: showAI ? alpha('#667eea', 0.1) : 'transparent',
                  '&:hover': { bgcolor: alpha('#667eea', 0.1) } }}>
                <SmartToyIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* ── STAT TILES ────────────────────────────────── */}
      {stats && (
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
          {statItems.map((item) => (
            <StatTile key={item.key} item={item}
              isActive={valuationFilter === item.key}
              onClick={item.clickable ? () => handleValuationCardClick(item.key) : undefined} />
          ))}
          {valuationFilter && (
            <Box sx={{ display: 'flex', alignItems: 'center', ml: 0.5 }}>
              <Chip size="small"
                label={`${valuationFilter === 'undervalued' ? t('vnstock.stocks.undervalued') : valuationFilter === 'neutral' ? t('vnstock.stocks.neutral') : t('vnstock.stocks.overvalued')} (${total})`}
                onDelete={() => setValuationFilter('')}
                deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                sx={{ height: 28, fontSize: '0.75rem', fontWeight: 600,
                  bgcolor: valuationFilter === 'undervalued' ? alpha('#22c55e', 0.1) : valuationFilter === 'neutral' ? alpha('#f59e0b', 0.1) : alpha('#ef4444', 0.1),
                  color: valuationFilter === 'undervalued' ? '#15803d' : valuationFilter === 'neutral' ? '#92400e' : '#991b1b',
                  border: '1px solid',
                  borderColor: valuationFilter === 'undervalued' ? alpha('#22c55e', 0.3) : valuationFilter === 'neutral' ? alpha('#f59e0b', 0.3) : alpha('#ef4444', 0.3) }}
              />
            </Box>
          )}
        </Box>
      )}

      {/* ── AI INSIGHTS ───────────────────────────────── */}
      <Collapse in={showAI && !valuationFilter} timeout={300}>
        <Box sx={{ mb: 2 }}>
          <AIInsightsSection aiInsights={aiInsights} aiLoading={aiLoading} valuationFilter={valuationFilter} />
        </Box>
      </Collapse>

      {errorMessage && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{errorMessage}</Alert>}

      {/* ── DATA TABLE ────────────────────────────────── */}
      <Paper elevation={0} sx={{
        borderRadius: 2.5, border: '1px solid #e5e7eb', overflow: 'hidden',
        transition: 'box-shadow 0.2s',
        '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.04)' },
      }}>
        {viewMode === VIEW_MODES.STOCKS ? (
          <StocksView stocks={stocks} total={total} page={page} rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage}
            onStockClick={handleStockClick} />
        ) : viewMode === VIEW_MODES.SECTORS ? (
          sectorsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
          ) : sectorsError ? (
            <Box sx={{ p: 4, textAlign: 'center' }}><Typography color="error">{sectorsError.message || 'Failed'}</Typography></Box>
          ) : sectorsData2.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}><Typography color="text.secondary">No sector data</Typography></Box>
          ) : (
            <SectorsView sectors={sectorsData2} />
          )
        ) : null}
      </Paper>
    </Box>
  );
};

export default StocksList;
